import React from 'react';

/* ------------------------------------------------------------------------- *
 * Skeleton primitives
 *
 * Replaces the spinner-style loaders used across the admin. A skeleton keeps
 * the page layout stable while data is in flight, so content does not jump in.
 * Nothing here renders copy — the shapes alone carry the "still loading" signal.
 * ------------------------------------------------------------------------- */

type SkeletonShape = 'text' | 'rect' | 'rounded' | 'circle';

export interface SkeletonProps {
  shape?: SkeletonShape;
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

const SHAPE_CLASSES: Record<SkeletonShape, string> = {
  text: 'rounded h-3',
  rect: 'rounded-none',
  rounded: 'rounded-lg',
  circle: 'rounded-full',
};

const size = (v: string | number | undefined) => (typeof v === 'number' ? `${v}px` : v);

export const Skeleton: React.FC<SkeletonProps> = ({
  shape = 'text',
  width,
  height,
  className = '',
  style,
}) => (
  <span
    aria-hidden="true"
    className={`relative block shrink-0 overflow-hidden bg-slate-200/80 dark:bg-slate-700/50 ${SHAPE_CLASSES[shape]} ${className}`}
    style={{ width: size(width), height: size(height), ...style }}
  >
    <span className="absolute inset-0 -translate-x-full animate-skeleton-sweep bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10" />
  </span>
);

/** A stack of text lines; the last line is shortened so it reads as a paragraph. */
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className = '',
}) => (
  <div className={`w-full space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className={i === lines - 1 ? 'w-2/3' : 'w-full'} />
    ))}
  </div>
);

/* ------------------------------------------------------------------------- *
 * Composites
 * ------------------------------------------------------------------------- */

/** Toolbar + header row + body rows — the default for any grid/list screen. */
export const TableSkeleton: React.FC<{
  rows?: number;
  columns?: number;
  showToolbar?: boolean;
  className?: string;
}> = ({ rows = 8, columns = 6, showToolbar = true, className = '' }) => (
  <div className={`w-full ${className}`} aria-busy="true">
    {showToolbar && (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Skeleton shape="rounded" className="h-9 w-64" />
        <div className="flex items-center gap-2">
          <Skeleton shape="rounded" className="h-9 w-24" />
          <Skeleton shape="rounded" className="h-9 w-24" />
        </div>
      </div>
    )}
    <div className="w-full overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0 dark:border-slate-800"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              className={`h-3 flex-1 ${c === 0 ? 'max-w-[18%]' : ''}`}
              style={{ opacity: 1 - r * 0.045 }}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Rows of label/value pairs — drawers, detail panes, summary sections. */
export const ListSkeleton: React.FC<{ rows?: number; avatar?: boolean; className?: string }> = ({
  rows = 6,
  avatar = true,
  className = '',
}) => (
  <div className={`w-full space-y-3 ${className}`} aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
      >
        {avatar && <Skeleton shape="circle" className="h-9 w-9" />}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-3 w-2/3" />
        </div>
        <Skeleton shape="rounded" className="h-6 w-16" />
      </div>
    ))}
  </div>
);

/** Responsive grid of cards. */
export const CardsSkeleton: React.FC<{ count?: number; columns?: number; className?: string }> = ({
  count = 6,
  columns = 3,
  className = '',
}) => (
  <div
    className={`grid w-full gap-4 ${
      columns >= 4
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
        : columns === 3
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          : 'grid-cols-1 sm:grid-cols-2'
    } ${className}`}
    aria-busy="true"
  >
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
      >
        <div className="flex items-center gap-3">
          <Skeleton shape="rounded" className="h-10 w-10" />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
        <SkeletonText lines={2} />
        <Skeleton shape="rounded" className="h-8 w-24" />
      </div>
    ))}
  </div>
);

/** KPI / stat tiles. */
export const StatsSkeleton: React.FC<{ count?: number; className?: string }> = ({
  count = 4,
  className = '',
}) => (
  <div
    className={`grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 ${className}`}
    aria-busy="true"
  >
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
      >
        <Skeleton shape="rounded" className="h-11 w-11" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
    ))}
  </div>
);

/** Chart placeholder with a plotted-bars silhouette. */
export const ChartSkeleton: React.FC<{ height?: number | string; className?: string }> = ({
  height = 260,
  className = '',
}) => (
  <div
    className={`w-full rounded-xl border border-slate-200 p-4 dark:border-slate-700 ${className}`}
    aria-busy="true"
  >
    <Skeleton className="mb-4 h-3.5 w-40" />
    <div className="flex items-end gap-2" style={{ height: size(height) }}>
      {[62, 88, 45, 74, 95, 55, 80, 38, 68, 90, 50, 72].map((h, i) => (
        <Skeleton key={i} shape="rounded" className="flex-1" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

/** Stat tiles + charts + a grid — the shape of most dashboards here. */
export const DashboardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`w-full space-y-5 ${className}`} aria-busy="true">
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-2">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-3 w-80" />
      </div>
      <Skeleton shape="rounded" className="h-9 w-32" />
    </div>
    <StatsSkeleton />
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <ChartSkeleton />
      <ChartSkeleton />
    </div>
    <TableSkeleton rows={5} showToolbar={false} />
  </div>
);

/** Labelled input fields laid out in columns. */
export const FormSkeleton: React.FC<{ fields?: number; columns?: number; className?: string }> = ({
  fields = 8,
  columns = 2,
  className = '',
}) => (
  <div className={`w-full space-y-5 ${className}`} aria-busy="true">
    <div className={`grid gap-4 ${columns >= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton shape="rounded" className="h-10 w-full" />
        </div>
      ))}
    </div>
    <div className="flex justify-end gap-2">
      <Skeleton shape="rounded" className="h-9 w-24" />
      <Skeleton shape="rounded" className="h-9 w-24" />
    </div>
  </div>
);

/** Header block + field pairs — record detail views and read-only panels. */
export const DetailSkeleton: React.FC<{ rows?: number; className?: string }> = ({
  rows = 8,
  className = '',
}) => (
  <div className={`w-full space-y-5 ${className}`} aria-busy="true">
    <div className="flex items-center gap-4">
      <Skeleton shape="rounded" className="h-14 w-14" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton shape="rounded" className="h-7 w-20" />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-3.5 w-2/3" />
        </div>
      ))}
    </div>
  </div>
);

/** Map / viewer canvas placeholder. */
export const MapSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`relative h-full min-h-[320px] w-full overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 ${className}`}
    aria-busy="true"
  >
    <Skeleton shape="rect" className="absolute inset-0 h-full w-full rounded-none" />
    <div className="absolute left-4 top-4 space-y-2">
      <Skeleton shape="rounded" className="h-8 w-40" />
      <Skeleton shape="rounded" className="h-24 w-40" />
    </div>
    <div className="absolute bottom-4 right-4 space-y-2">
      <Skeleton shape="rounded" className="h-8 w-8" />
      <Skeleton shape="rounded" className="h-8 w-8" />
    </div>
  </div>
);

/** Tree / hierarchy navigator placeholder. */
export const TreeSkeleton: React.FC<{ rows?: number; className?: string }> = ({
  rows = 8,
  className = '',
}) => (
  <div className={`w-full space-y-2.5 ${className}`} aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-2" style={{ paddingLeft: (i % 3) * 18 }}>
        <Skeleton shape="rounded" className="h-4 w-4" />
        <Skeleton className="h-3 flex-1" style={{ maxWidth: `${70 - (i % 3) * 12}%` }} />
      </div>
    ))}
  </div>
);

/** Generic page fallback: title block over a grid. */
export const PageSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`w-full space-y-5 p-1 ${className}`} aria-busy="true">
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-2">
        <Skeleton className="h-5 w-52" />
        <Skeleton className="h-3 w-72" />
      </div>
      <Skeleton shape="rounded" className="h-9 w-28" />
    </div>
    <TableSkeleton rows={7} />
  </div>
);

/** Whole-app shell used while the router/session boots. */
export const AppSkeleton: React.FC = () => (
  <div className="flex h-screen w-full overflow-hidden bg-white dark:bg-slate-900" aria-busy="true">
    <div className="hidden w-64 shrink-0 flex-col gap-4 border-r border-slate-200 p-4 dark:border-slate-700 md:flex">
      <div className="flex items-center gap-3">
        <Skeleton shape="circle" className="h-10 w-10" />
        <Skeleton className="h-4 flex-1" />
      </div>
      <div className="space-y-2.5 pt-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton shape="rounded" className="h-5 w-5" />
            <Skeleton className="h-3 flex-1" style={{ maxWidth: `${85 - (i % 4) * 10}%` }} />
          </div>
        ))}
      </div>
    </div>
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-700">
        <Skeleton className="h-4 w-64" />
        <div className="flex items-center gap-3">
          <Skeleton shape="rounded" className="h-9 w-9" />
          <Skeleton shape="circle" className="h-9 w-9" />
        </div>
      </div>
      <div className="flex-1 overflow-hidden p-6">
        <DashboardSkeleton />
      </div>
    </div>
  </div>
);

export default Skeleton;
